import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  forwardRef,
  useCallback,
} from "react";
import {
  Card,
  Spin,
  Empty,
  Typography,
  Tag,
  Tooltip,
  Row,
  Col,
  Statistic,
  Button,
  Select,
  Space,
  message,
} from "antd";
import {
  CarOutlined,
  DashboardOutlined,
  AimOutlined,
  BankOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  EnvironmentOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import axios from "../api/axiosConfig";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const { Text } = Typography;
const { Option } = Select;

// Colores para el ranking
const getRankingColor = (rank) => {
  if (rank <= 3) return "#f5222d"; // Rojo para los primeros 3
  if (rank <= 6) return "#fa8c16"; // Naranja para 4-6
  if (rank <= 10) return "#faad14"; // Amarillo para 7-10
  return "#52c41a";
};

// Función para truncar texto largo
const truncateText = (text, maxLength = 25) => {
  if (!text) return "";
  return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
};

// Mapeo de nombres de sectores a nombres personalizados
const sectorNameMapping = {
  "Atahualpa E": "San Bartolo",
  "San Vicente": "Miravalle",
  "Conjunto Habitacional Bonsai II": "La Armenia",
  Susanita: "Pascuales/Bastión Popular",
  Orellana: "Cuartel Modelo",
  "Barrio del Maestro": "Barrio del Maestro/Mall del Sur",
  "Avenida Leopoldo Carrera Calvo": "Colinas de los Ceibos",
  "Rodrigo Chávez González": "Lomas de Urdesa",
  "Avenida Presidente Carlos Julio Arosemena Tola": "Universidad Católica",
  Guayacanes: "Urdesa",
  URDESA: "Urdesa",
  "MARTHA DE ROLDOS": "Martha de Roldos",
  "CIUDAD COLÓN": "Ciudad Colón",
};

// Función para obtener el nombre personalizado de un sector
const getCustomSectorName = (sectorName) => {
  if (!sectorName) return null;

  // Buscar coincidencias exactas
  if (sectorNameMapping[sectorName]) {
    return sectorNameMapping[sectorName];
  }

  // Buscar coincidencias parciales
  for (const [original, custom] of Object.entries(sectorNameMapping)) {
    if (sectorName.includes(original)) {
      return custom;
    }
  }

  // Si no hay coincidencias, devolver el nombre original
  return sectorName;
};

// Componente memoizado para las tarjetas de vías para evitar re-renderizaciones innecesarias
const RoadCard = React.memo(function RoadCard({ road, index, onCenterMap }) {
  // Referencias para los componentes que necesitan tooltips
  const cityTagRef = useRef(null);
  const sectorTagRef = useRef(null);
  const densityStatRef = useRef(null);
  const vehiclesStatRef = useRef(null);
  const speedStatRef = useRef(null);
  const mapButtonRef = useRef(null);

  // Función para centrar en el mapa
  function handleCenterOnMap(e) {
    e.stopPropagation(); // Evitar propagación del evento

    // Crear objeto de ubicación para centrar el mapa
    const location = {
      lat: road.latitude,
      lng: road.longitude,
      name: road.street_name,
      province: road.province,
      zoomLevel: "cercano", // Usar el nivel de zoom cercano para ver la calle en detalle
    };

    // Llamar a la función proporcionada por el componente padre
    onCenterMap(location);
  }

  // Determinar la ciudad a mostrar
  let city = "Desconocido";
  if (road.state_name === "Guayas") {
    city = "Guayaquil";
  } else if (road.state_name) {
    city = road.state_name;
  } else if (road.display_name && typeof road.display_name === "string") {
    const parts = road.display_name.split(",");

    // Buscar "Guayaquil" o "Quito" en las partes del display_name
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i].trim();
      if (part === "Guayaquil" || part === "Quito") {
        city = part;
        break;
      }
    }

    // Si no encontramos la ciudad específica, usar la posición 3 como fallback
    if (city === "Desconocido" && parts.length >= 4) {
      city = parts[3].trim();
    }
  }

  // Determinar el sector a mostrar (tercer elemento del display_name)
  let sector = null;
  if (road.display_name && typeof road.display_name === "string") {
    const parts = road.display_name.split(",");
    if (parts.length >= 3) {
      sector = parts[2].trim();

      // Aplicar el mapeo personalizado de nombres de sectores
      sector = getCustomSectorName(sector);
    }
  }

  // Componentes personalizados con referencias para evitar findDOMNode
  const CityTag = forwardRef((props, ref) => (
    <Tag
      ref={ref}
      color="blue"
      style={{ borderRadius: "4px", padding: "2px 8px" }}
    >
      <BankOutlined /> {truncateText(city, 20)}
    </Tag>
  ));

  const SectorTag = forwardRef((props, ref) => (
    <Tag
      ref={ref}
      color="green"
      style={{ borderRadius: "4px", padding: "2px 8px" }}
    >
      <EnvironmentOutlined /> {truncateText(sector, 20)}
    </Tag>
  ));

  // Componentes de estadísticas con refs
  const DensityStat = forwardRef((props, ref) => (
    <div ref={ref}>
      <Statistic
        value={road.point_count}
        valueStyle={{
          fontSize: "16px",
          fontWeight: "600",
          color: "#1890ff",
        }}
        prefix={<AimOutlined style={{ color: "#1890ff" }} />}
        suffix={<small style={{ fontSize: "12px", opacity: 0.8 }}>Pasos</small>}
      />
    </div>
  ));

  const VehiclesStat = forwardRef((props, ref) => (
    <div ref={ref}>
      <Statistic
        value={road.unique_vehicles}
        valueStyle={{
          fontSize: "16px",
          fontWeight: "600",
          color: "#52c41a",
        }}
        prefix={<CarOutlined style={{ color: "#52c41a" }} />}
      />
    </div>
  ));

  const SpeedStat = forwardRef((props, ref) => (
    <div ref={ref}>
      <Statistic
        value={road.avg_speed}
        valueStyle={{
          fontSize: "16px",
          fontWeight: "600",
          color: "#fa8c16",
        }}
        prefix={<DashboardOutlined style={{ color: "#fa8c16" }} />}
        suffix={<small style={{ fontSize: "12px", opacity: 0.8 }}>km/h</small>}
      />
    </div>
  ));

  const MapButton = forwardRef((props, ref) => (
    <Button
      ref={ref}
      type="primary"
      shape="circle"
      icon={<EnvironmentOutlined />}
      size="large"
      style={{
        boxShadow: "0 2px 6px rgba(24, 144, 255, 0.3)",
        background: "#1890ff",
        border: "none",
      }}
      onClick={(e) => {
        e.stopPropagation();
        handleCenterOnMap(e);
      }}
    />
  ));

  return (
    <Card
      className="road-card"
      style={{
        marginBottom: "12px",
        borderRadius: "8px",
        borderLeft: `4px solid ${getRankingColor(road.ranking)}`,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        transition: "all 0.3s ease",
        cursor: "pointer",
        overflow: "hidden",
      }}
      styles={{
        body: { padding: "16px" },
      }}
      hoverable
      onClick={handleCenterOnMap}
    >
      <Row gutter={16} align="middle">
        <Col span={2}>
          <div
            style={{
              background: getRankingColor(road.ranking),
              color: "white",
              borderRadius: "50%",
              width: "36px",
              height: "36px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "bold",
              fontSize: "16px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
            }}
          >
            {road.ranking || index + 1}
          </div>
        </Col>

        <Col span={10}>
          <div>
            <Text
              strong
              style={{
                fontSize: "16px",
                display: "block",
                marginBottom: "4px",
              }}
            >
              {road.street_name}
            </Text>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "6px",
                marginTop: "6px",
              }}
            >
              {city && (
                <Tooltip title={city}>
                  <CityTag ref={cityTagRef} />
                </Tooltip>
              )}
              {sector && (
                <Tooltip title={sector}>
                  <SectorTag ref={sectorTagRef} />
                </Tooltip>
              )}
            </div>
          </div>
        </Col>

        <Col span={9}>
          <Row gutter={[12, 0]}>
            <Col span={8}>
              <Tooltip title="Densidad de tráfico">
                <DensityStat ref={densityStatRef} />
              </Tooltip>
            </Col>
            <Col span={8}>
              <Tooltip title="Vehículos">
                <VehiclesStat ref={vehiclesStatRef} />
              </Tooltip>
            </Col>
            <Col span={8}>
              <Tooltip title="Velocidad promedio">
                <SpeedStat ref={speedStatRef} />
              </Tooltip>
            </Col>
          </Row>
        </Col>

        <Col span={3} style={{ textAlign: "right" }}>
          <Tooltip title="Ver en mapa">
            <MapButton ref={mapButtonRef} />
          </Tooltip>
        </Col>
      </Row>
    </Card>
  );
});

// Componente principal del panel
function RoadsTrafficPanel({ onCenterMap }) {
  // Estados principales
  const [activeProvince, setActiveProvince] = useState("Guayas");
  const [sortOrder, setSortOrder] = useState("ranking");
  const [loadingRoads, setLoadingRoads] = useState(true);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [error, setError] = useState(null);
  const [provinceData, setProvinceData] = useState({
    Guayas: [],
    Pichincha: [],
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Función para obtener datos
  const fetchData = useCallback(async () => {
    try {
      setLoadingRoads(true);
      setError(null);

      // Cargar datos en paralelo para mayor velocidad
      const [guayasResponse, pichinchResponse] = await Promise.all([
        axios.get("/positions/most-transited-roads", {
          params: {
            province: "Guayas",
            limit: 15,
          },
        }),
        axios.get("/positions/most-transited-roads", {
          params: {
            province: "Pichincha",
            limit: 15,
          },
        }),
      ]);

      // Procesar datos inmediatamente
      const guayasData = processRoadData(guayasResponse.data || [], "Guayas");
      const pichinchData = processRoadData(
        pichinchResponse.data || [],
        "Pichincha"
      );

      // Verificar si hay datos
      if (guayasData.length === 0 && pichinchData.length === 0) {
        message.warning(
          "No se encontraron vías transitadas en el período seleccionado. Intente con un rango de fechas más amplio."
        );
      }

      // Guardar datos de ambas provincias
      setProvinceData({
        Guayas: guayasData,
        Pichincha: pichinchData,
      });

      setInitialLoadDone(true);
      setLoadingRoads(false);
    } catch (error) {
      console.error("Error al obtener vías más transitadas:", error);

      // Mostrar mensaje de error más descriptivo
      if (error.response) {
        if (error.response.status === 500) {
          message.error(
            "Error en el servidor al obtener datos de vías transitadas. Por favor, intente más tarde."
          );
        } else if (error.response.status === 404) {
          message.error(
            "No se pudo encontrar el servicio de vías transitadas."
          );
        } else {
          message.error(
            `Error al obtener datos: ${
              error.response.data?.message || "Error desconocido"
            }`
          );
        }
      } else if (error.request) {
        message.error(
          "No se pudo conectar con el servidor. Verifique su conexión a internet."
        );
      } else {
        message.error(
          "Error al procesar la solicitud. Por favor, intente nuevamente."
        );
      }

      setLoadingRoads(false);
      setError("Error al obtener datos. Por favor, intente más tarde.");
    }
  }, []);

  // Cargar datos iniciales
  useEffect(() => {
    if (!initialLoadDone) {
      fetchData();
    }
  }, [initialLoadDone, fetchData]);

  // Función para cambiar de provincia
  const handleProvinceChange = (province) => {
    // Si ya estamos en la provincia seleccionada, no hacer nada
    if (province === activeProvince) return;

    // Resetear los contadores de elementos visibles al cambiar de provincia
    setActiveProvince(province);

    // Centrar el mapa en la nueva provincia seleccionada
    const provinceCoordinates = {
      Guayas: { lat: -2.1894, lng: -79.889 }, // Coordenadas de Guayaquil
      Pichincha: { lat: -0.1807, lng: -78.4678 }, // Coordenadas de Quito
    };

    const coordinates = provinceCoordinates[province];
    if (coordinates && onCenterMap) {
      onCenterMap({
        lat: coordinates.lat,
        lng: coordinates.lng,
        zoomLevel: "lejano", // Usar el nivel de zoom lejano para ver toda la provincia
        name: province === "Guayas" ? "Guayaquil" : "Quito",
        province: province,
      });
    }
  };

  // Función para cambiar el orden
  const handleSortChange = (value) => {
    setSortOrder(value);
  };

  // Procesar datos de carreteras (helper function)
  const processRoadData = (data, province) => {
    if (!data || !data.length) return [];

    return data.map((road, index) => ({
      ...road,
      province,
      ranking: index + 1,
    }));
  };

  // Memorizar las vías ordenadas para ambas provincias
  const sortedRoadsByProvince = useMemo(() => {
    if (!initialLoadDone) return { Guayas: [], Pichincha: [] };

    // Función para ordenar - solo se ejecuta cuando cambia la orden
    const applySorting = (roads) => {
      // Crear una copia para no mutar el array original
      const roadsCopy = [...roads];

      switch (sortOrder) {
        case "vehicles-desc":
          roadsCopy.sort((a, b) => b.unique_vehicles - a.unique_vehicles);
          break;
        case "vehicles-asc":
          roadsCopy.sort((a, b) => a.unique_vehicles - b.unique_vehicles);
          break;
        case "density-desc":
          roadsCopy.sort((a, b) => b.point_count - a.point_count);
          break;
        case "density-asc":
          roadsCopy.sort((a, b) => a.point_count - b.point_count);
          break;
        default:
          // Por defecto ordenamos por ranking
          roadsCopy.sort((a, b) => a.ranking - b.ranking);
          break;
      }

      return roadsCopy;
    };

    // Solo ordenamos las provincias cuando es necesario
    return {
      Guayas: applySorting(provinceData.Guayas || []),
      Pichincha: applySorting(provinceData.Pichincha || []),
    };
  }, [initialLoadDone, provinceData, sortOrder]);

  // Datos actualmente mostrados en la lista
  const displayedRoads = useMemo(() => {
    return sortedRoadsByProvince[activeProvince] || [];
  }, [sortedRoadsByProvince, activeProvince]);

  // Resetear página cuando cambia de provincia
  useEffect(() => {
    setCurrentPage(1);
  }, [activeProvince]);

  // Renderizado de la lista de vías con paginación
  const renderRoadsList = useMemo(() => {
    if (error) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
            textAlign: "center",
          }}
        >
          <Empty
            description={
              <div>
                <p style={{ marginBottom: "16px" }}>{error}</p>
                <Button
                  type="primary"
                  onClick={() => {
                    setError(null);
                    fetchData();
                  }}
                >
                  Reintentar
                </Button>
              </div>
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      );
    }

    if (!displayedRoads || displayedRoads.length === 0) {
      return (
        <Empty
          description={
            <div>
              <p>No hay datos disponibles para {activeProvince}</p>
              <Button
                type="primary"
                onClick={fetchData}
                style={{ marginTop: "16px" }}
              >
                Reintentar
              </Button>
            </div>
          }
          style={{ margin: "30px 0" }}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      );
    }

    // Calcular datos paginados
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedRoads = displayedRoads.slice(startIndex, endIndex);
    const totalPages = Math.ceil(displayedRoads.length / itemsPerPage);

    // Resetear a página 1 cuando cambia la provincia
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: "100%",
        }}
      >
        <div
          className="roads-list"
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            flex: 1,
          }}
        >
          {paginatedRoads.map((road) => (
            <RoadCard
              key={`road-${road.street_name}-${road.ranking}-${road.unique_vehicles}`}
              road={road}
              index={road.ranking - 1}
              onCenterMap={onCenterMap}
            />
          ))}
        </div>
        {totalPages > 1 && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "8px",
              padding: "12px",
              borderTop: "1px solid #e8e8e8",
              backgroundColor: "#fafafa",
            }}
          >
            <Button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              ← Anterior
            </Button>
            <span style={{ margin: "0 8px", color: "#666" }}>
              Página {currentPage} de {totalPages}
            </span>
            <Button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Siguiente →
            </Button>
          </div>
        )}
      </div>
    );
  }, [displayedRoads, activeProvince, onCenterMap, error, fetchData, currentPage, itemsPerPage]);

  // Función para exportar a Excel
  const exportToExcel = () => {
    try {
      if (!displayedRoads || displayedRoads.length === 0) {
        message.warning("No hay datos para exportar");
        return;
      }

      // Preparar los datos para Excel
      const excelData = displayedRoads.map((road) => {
        // Obtener el sector y la ciudad
        let city = "Desconocido";
        if (road.state_name === "Guayas") {
          city = "Guayaquil";
        } else if (road.state_name) {
          city = road.state_name;
        }

        let sector = "";
        if (road.display_name && typeof road.display_name === "string") {
          const parts = road.display_name.split(",");
          if (parts.length >= 3) {
            sector = parts[2].trim();
            sector = getCustomSectorName(sector);
          }
        }

        // Crear objeto con los datos para Excel
        return {
          Ranking: road.ranking,
          Vía: road.street_name,
          Ciudad: city,
          Sector: sector || "N/A",
          "Densidad de Tráfico": road.point_count,
          Vehículos: road.unique_vehicles,
          "Velocidad Promedio (km/h)": parseFloat(road.avg_speed).toFixed(1),
          Latitud: road.latitude,
          Longitud: road.longitude,
        };
      });

      // Crear libro de Excel
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Vías Transitadas");

      // Ajustar ancho de columnas
      const columnWidths = [
        { wch: 8 }, // Ranking
        { wch: 30 }, // Vía
        { wch: 15 }, // Ciudad
        { wch: 20 }, // Sector
        { wch: 15 }, // Densidad de Tráfico
        { wch: 10 }, // Vehículos
        { wch: 20 }, // Velocidad Promedio
        { wch: 12 }, // Latitud
        { wch: 12 }, // Longitud
      ];
      worksheet["!cols"] = columnWidths;

      // Generar archivo Excel
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      const data = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // Nombre del archivo con fecha y provincia
      const fileName = `Vias_Transitadas_${activeProvince}_${
        new Date().toISOString().split("T")[0]
      }.xlsx`;

      // Guardar archivo
      saveAs(data, fileName);

      message.success(`Datos exportados a ${fileName}`);
    } catch (error) {
      console.error("Error al exportar a Excel:", error);
      message.error("Error al exportar a Excel");
    }
  };

  // Renderizar el componente final
  return (
    <div
      className="roads-traffic-panel"
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        width: "600px", // Aumentar el ancho del panel
        maxWidth: "100%",
        boxShadow: "0 0 10px rgba(0,0,0,0.1)",
        overflow: "hidden", // Asegurar que no haya desbordamiento en el panel principal
      }}
    >
      <div
        className="selector-container"
        style={{
          display: "flex",
          justifyContent: "space-between",
          padding: "16px",
          backgroundColor: "#fff",
          flexShrink: 0,
          borderBottom: "1px solid #f0f0f0",
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <h3
            style={{
              margin: 0,
              fontSize: "16px",
              fontWeight: "600",
              color: "#1890ff",
            }}
          >
            Vías más transitadas
          </h3>
        </div>
        <Space size="middle">
          <Select
            value={activeProvince}
            style={{
              width: 130,
              borderRadius: "6px",
            }}
            onChange={handleProvinceChange}
            loading={loadingRoads && !initialLoadDone}
            disabled={loadingRoads && !initialLoadDone}
            dropdownStyle={{ borderRadius: "6px" }}
          >
            <Option value="Guayas">Guayas</Option>
            <Option value="Pichincha">Pichincha</Option>
          </Select>
          <Select
            value={sortOrder}
            style={{
              width: 160,
              borderRadius: "6px",
            }}
            onChange={handleSortChange}
            disabled={loadingRoads}
            placeholder="Ordenar"
            dropdownStyle={{ borderRadius: "6px" }}
          >
            <Option value="ranking">
              <span style={{ display: "flex", alignItems: "center" }}>
                <SortDescendingOutlined style={{ marginRight: "5px" }} />{" "}
                Ordenar
              </span>
            </Option>
            <Option value="vehicles-desc">
              <span style={{ display: "flex", alignItems: "center" }}>
                <SortDescendingOutlined style={{ marginRight: "5px" }} />{" "}
                Vehículos ↑
              </span>
            </Option>
            <Option value="vehicles-asc">
              <span style={{ display: "flex", alignItems: "center" }}>
                <SortAscendingOutlined style={{ marginRight: "5px" }} />{" "}
                Vehículos ↓
              </span>
            </Option>
            <Option value="density-desc">
              <span style={{ display: "flex", alignItems: "center" }}>
                <SortDescendingOutlined style={{ marginRight: "5px" }} />{" "}
                Densidad ↑
              </span>
            </Option>
            <Option value="density-asc">
              <span style={{ display: "flex", alignItems: "center" }}>
                <SortAscendingOutlined style={{ marginRight: "5px" }} />{" "}
                Densidad ↓
              </span>
            </Option>
          </Select>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={exportToExcel}
            disabled={loadingRoads || !displayedRoads.length}
            style={{
              borderRadius: "6px",
              background: "#52c41a",
              border: "none",
            }}
            title="Exportar a Excel"
          >
            Exportar
          </Button>
        </Space>
      </div>
      <div
        style={{
          flex: 1,
          position: "relative",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          backgroundColor: "#f9f9f9",
        }}
      >
        <div
          style={{
            flex: 1,
            overflow: "auto",
            padding: "16px",
            position: "relative",
            height: "100%",
            maxHeight: "calc(100vh - 60px)",
          }}
          className="roads-container"
        >
          {loadingRoads ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: "50px 0",
              }}
            >
              <Spin size="large">
                <div
                  style={{
                    marginTop: "15px",
                    color: "#1890ff",
                    textAlign: "center",
                  }}
                >
                  Cargando vías...
                </div>
              </Spin>
            </div>
          ) : (
            renderRoadsList
          )}
        </div>
      </div>
    </div>
  );
}

export default RoadsTrafficPanel;
